import type { MeetingBasedEventTypeId } from '@ps/isp-web-services-types';
import type { TranslationTag } from '@ps/web-core-intl';

import { EventTypeId } from '@swf/sports/types/eventType';
import { isMeetingBasedEventTypeId } from '@swf/sports/utils/isEventTypeId';

import type { SectionWidgetConfig } from '../../types';
import { SportsWidgetType } from '../../widgets';
import { buildSportsLayoutPageResponse } from '../buildSportsLayoutPageResponse';

import {
  TXTWCL_Bet_on_greyhound_racing_with_P_ELL,
  TXTWCL_Bet_on_horse_racing_with_Poker_ELL,
  TXTWCL_Greyhound_Racing_Betting_X_Gre_ELL,
  TXTWCL_Horse_Racing_Betting_X_Horse_R_ELL
} from './sportsAnimalRacingNext.messages.json';

const titleMap: Record<MeetingBasedEventTypeId, TranslationTag> = {
  [EventTypeId.HORSE_RACING]: TXTWCL_Horse_Racing_Betting_X_Horse_R_ELL,
  [EventTypeId.GREYHOUND_RACING]: TXTWCL_Greyhound_Racing_Betting_X_Gre_ELL
};

const descriptionMap: Record<MeetingBasedEventTypeId, TranslationTag> = {
  [EventTypeId.HORSE_RACING]: TXTWCL_Bet_on_horse_racing_with_Poker_ELL,
  [EventTypeId.GREYHOUND_RACING]: TXTWCL_Bet_on_greyhound_racing_with_P_ELL
};

const sportsAnimalRacingNextWidgets = ({
  isLandingPagePopularAccumulatorsEnabled
}: {
  isLandingPagePopularAccumulatorsEnabled: boolean;
}) =>
  [
    {
      title: 'Sports CMS Banner Widget',
      _content_type_uid: SportsWidgetType.SportsCmsBanner
    },
    {
      title: 'Sports Event Type Info Widget',
      _content_type_uid: SportsWidgetType.SportsEventTypeInfo
    },
    ...(isLandingPagePopularAccumulatorsEnabled
      ? [
          {
            title: 'Sports Animal Racing Landing Page Widget',
            _content_type_uid: SportsWidgetType.SportsAnimalRacingLandingPage
          } satisfies SectionWidgetConfig
        ]
      : []),
    {
      title: 'Sports Next Races Widget',
      _content_type_uid: SportsWidgetType.SportsNextRaces,
      maxRunners: 3,
      condensed: false
    }
  ] satisfies readonly SectionWidgetConfig[];

export const sportsAnimalRacingNext = ({
  eventTypeId,
  isLandingPagePopularAccumulatorsEnabled
}: {
  eventTypeId: EventTypeId;
  isLandingPagePopularAccumulatorsEnabled: boolean;
}) => {
  const titleTag = isMeetingBasedEventTypeId(eventTypeId)
    ? titleMap[eventTypeId]
    : TXTWCL_Horse_Racing_Betting_X_Horse_R_ELL;

  const descriptionTag = isMeetingBasedEventTypeId(eventTypeId)
    ? descriptionMap[eventTypeId]
    : TXTWCL_Bet_on_horse_racing_with_Poker_ELL;

  return buildSportsLayoutPageResponse({
    alias: 'sportsAnimalRacingNext',
    seoConfig: {
      apiPath: '/meta/event-type/',
      descriptionTag,
      redirectTo: 'sportsHome',
      redirectStatusCode: 302,
      titleTag
    },
    mainSectionWidgetConfigs: sportsAnimalRacingNextWidgets({
      isLandingPagePopularAccumulatorsEnabled
    })
  });
};
